import React from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  SimpleGrid,
  Spinner,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
} from '@chakra-ui/react';
import { FiMenu, FiRefreshCw, FiDownload } from 'react-icons/fi';
import * as XLSX from 'xlsx-js-style';
import SaktiFaceRegistryApiService from '../services/saktiFaceRegistryApiService';
import SaktiSidebar from '../components/SaktiSidebar';
import SaktiFaceRegistryFilterBar from '../components/SaktiFaceRegistryFilterBar';
import SaktiFaceRegistryImageViewer from '../components/SaktiFaceRegistryImageViewer';
import { useSaktiFaceRegistry } from '../hooks/useSaktiFaceRegistry';
import { SAKTI_UI_SIZE } from '../uiSizes';
import { trackDownload } from '../../activity';

const SaktiFaceRegistryPage: React.FC = () => {
  const registry = useSaktiFaceRegistry();

  const {
    isOpen: isMobileSidebarOpen,
    onOpen: onMobileSidebarOpen,
    onClose: onMobileSidebarClose,
  } = useDisclosure();

  const isDrawerOpen = !!registry.selectedEmployee;

  const [downloading, setDownloading] = React.useState(false);
  const { isOpen: isExcelOpen, onOpen: onExcelOpen, onClose: onExcelClose } = useDisclosure();

  // Excel popup states
  const [excelEstateOdooId, setExcelEstateOdooId] = React.useState<number | undefined>(undefined);
  const [excelDivisionOdooId, setExcelDivisionOdooId] = React.useState<number | undefined>(undefined);
  const [excelJobPosition, setExcelJobPosition] = React.useState<string | undefined>(undefined);

  // Filter divisions for the modal
  const selectedExcelEstate = excelEstateOdooId
    ? registry.estates.find((e) => e.odoo_id === excelEstateOdooId)
    : undefined;
  const excelDivisions = selectedExcelEstate
    ? registry.allDivisions.filter((d) => d.estate_id === selectedExcelEstate.id)
    : registry.allDivisions;

  const handleModalEstateChange = (val: string) => {
    const odooId = val ? Number(val) : undefined;
    setExcelEstateOdooId(odooId);
    setExcelDivisionOdooId(undefined); // Reset division on estate change
  };

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const data = await SaktiFaceRegistryApiService.getEmployees({
        page: 1,
        limit: 50000,
        estateOdooId: excelEstateOdooId,
        divisionOdooId: excelDivisionOdooId,
        jobPosition: excelJobPosition,
        search: '',
        enrolledOnly: false,
      });

      const excelData = [
        ['No', 'Nama', 'NIP', 'Estate', 'Divisi', 'Jabatan', 'Status', 'Jumlah Foto']
      ];

      data.data.forEach((emp, index) => {
        excelData.push([
          (index + 1).toString(),
          emp.name,
          emp.nip,
          emp.estate_name || '-',
          emp.division_name || '-',
          emp.job_position || '-',
          emp.enrolled ? 'Terdaftar' : 'Belum Terdaftar',
          emp.image_count.toString()
        ]);
      });

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);
      
      const maxLens = excelData[0].map((_, colIndex) => 
        Math.max(...excelData.map(row => (row[colIndex] || '').toString().length))
      );
      worksheet['!cols'] = maxLens.map(len => ({ wch: len + 3 }));

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Face Registry Employees');
      
      let filename = 'Face_Registry_Employees';
      if (excelEstateOdooId) {
        const est = registry.estates.find(e => e.odoo_id === excelEstateOdooId);
        if (est) filename += `_${est.name.replace(/\s+/g, '_')}`;
      }
      if (excelDivisionOdooId) {
        const div = registry.allDivisions.find(d => d.odoo_id === excelDivisionOdooId);
        if (div) filename += `_${div.name.replace(/\s+/g, '_')}`;
      }
      if (excelJobPosition) {
        filename += `_${excelJobPosition.replace(/\s+/g, '_')}`;
      }
      filename += '.xlsx';

      XLSX.writeFile(workbook, filename);
      trackDownload('Sakti Face Registry', { filename });
      onExcelClose();
    } catch (err) {
      console.error('Error generating excel:', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box minH="100vh" bg="#ECEEF1">
      <Flex align="stretch">
        {/* Desktop Sidebar */}
        <Box display={{ base: 'none', xl: 'block' }}>
          <SaktiSidebar activeMenu="face-registry" />
        </Box>

        {/* Mobile Sidebar Drawer */}
        <Drawer isOpen={isMobileSidebarOpen} placement="left" onClose={onMobileSidebarClose} size="xs">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton zIndex={10} color="gray.500" />
            <DrawerBody p={0}>
              <SaktiSidebar activeMenu="face-registry" />
            </DrawerBody>
          </DrawerContent>
        </Drawer>

        <Box flex={1} p={SAKTI_UI_SIZE.pagePadding} minW={0}>
          <VStack align="stretch" spacing={SAKTI_UI_SIZE.sectionGap}>
            {/* Header */}
            <HStack spacing={3} align="center">
              <IconButton
                aria-label="Open menu"
                icon={<FiMenu />}
                variant="outline"
                bg="white"
                display={{ base: 'flex', xl: 'none' }}
                onClick={onMobileSidebarOpen}
              />
              <Text fontSize={SAKTI_UI_SIZE.pageTitle} fontWeight="bold" color="gray.900" lineHeight="shorter">
                Face Registry
              </Text>
              <IconButton
                aria-label="Refresh"
                icon={<FiRefreshCw />}
                size="sm"
                variant="outline"
                bg="white"
                isLoading={registry.loadingEmployees}
                onClick={registry.reloadEmployees}
              />
              <Button
                leftIcon={<FiDownload />}
                size="sm"
                variant="outline"
                bg="white"
                onClick={onExcelOpen}
              >
                Unduh Excel
              </Button>
            </HStack>

            {/* Stats */}
            {registry.stats && (
              <Box bg="white" borderRadius="lg" p={4} borderWidth="1px" borderColor="gray.200">
                <StatGroup>
                  <Stat>
                    <StatLabel fontSize="xs" color="gray.500">Total Karyawan</StatLabel>
                    <StatNumber fontSize="xl">{registry.stats.total_employees}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel fontSize="xs" color="gray.500">Terdaftar</StatLabel>
                    <StatNumber fontSize="xl" color="green.500">{registry.stats.enrolled_count}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel fontSize="xs" color="gray.500">Belum Terdaftar</StatLabel>
                    <StatNumber fontSize="xl" color="orange.400">{registry.stats.unenrolled_count}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel fontSize="xs" color="gray.500">Total Foto</StatLabel>
                    <StatNumber fontSize="xl">{registry.stats.total_images}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel fontSize="xs" color="gray.500">Total Batch</StatLabel>
                    <StatNumber fontSize="xl">{registry.stats.total_batches}</StatNumber>
                  </Stat>
                </StatGroup>
              </Box>
            )}

            {/* Filter Bar */}
            <SaktiFaceRegistryFilterBar
              filters={registry.filters}
              estates={registry.estates}
              divisions={registry.divisions}
              jobPositions={registry.jobPositions}
              onSearchChange={registry.setSearch}
              onEstateOdooIdChange={registry.setEstateOdooId}
              onDivisionOdooIdChange={registry.setDivisionOdooId}
              onJobPositionChange={registry.setJobPosition}
              onEnrolledOnlyChange={registry.setEnrolledOnly}
            />

            {/* Employee Table */}
            <Box bg="white" borderRadius="lg" borderWidth="1px" borderColor="gray.200" overflow="hidden">
              {registry.employeesError && (
                <Alert status="error" m={4} borderRadius="md">
                  <AlertIcon />
                  {registry.employeesError}
                </Alert>
              )}
              {registry.loadingEmployees ? (
                <Flex justify="center" align="center" p={10}>
                  <Spinner color="customGreen.500" />
                </Flex>
              ) : (
                <>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Nama</Th>
                          <Th>NIP</Th>
                          <Th>Estate</Th>
                          <Th>Divisi</Th>
                          <Th>Jabatan</Th>
                          <Th>Status</Th>
                          <Th isNumeric>Foto</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {(registry.employeeList?.data ?? []).map((emp) => (
                          <Tr
                            key={emp.employee_id}
                            cursor="pointer"
                            _hover={{ bg: 'gray.50' }}
                            onClick={() => registry.selectEmployee(emp)}
                            bg={registry.selectedEmployee?.employee_id === emp.employee_id ? 'green.50' : undefined}
                          >
                            <Td fontWeight="medium" maxW="200px">
                              <Text noOfLines={1}>{emp.name}</Text>
                            </Td>
                            <Td color="gray.600" fontSize="xs">{emp.nip}</Td>
                            <Td fontSize="xs">{emp.estate_name || '-'}</Td>
                            <Td fontSize="xs">{emp.division_name || '-'}</Td>
                            <Td fontSize="xs">{emp.job_position || '-'}</Td>
                            <Td>
                              <Badge colorScheme={emp.enrolled ? 'green' : 'orange'} fontSize="10px">
                                {emp.enrolled ? 'Terdaftar' : 'Belum'}
                              </Badge>
                            </Td>
                            <Td isNumeric fontSize="sm" color={emp.image_count > 0 ? 'green.600' : 'gray.400'}>
                              {emp.image_count}
                            </Td>
                          </Tr>
                        ))}
                        {(registry.employeeList?.data ?? []).length === 0 && (
                          <Tr>
                            <Td colSpan={7} textAlign="center" py={8} color="gray.400">
                              Tidak ada data
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </Box>

                  {/* Pagination */}
                  {registry.employeeList && registry.employeeList.total_pages > 1 && (
                    <HStack justify="space-between" p={3} borderTopWidth="1px" borderColor="gray.100">
                      <Text fontSize="xs" color="gray.500">
                        {registry.employeeList.total} karyawan
                      </Text>
                      <HStack spacing={1}>
                        <Button
                          size="xs"
                          variant="outline"
                          isDisabled={registry.filters.page <= 1}
                          onClick={() => registry.goToPage(registry.filters.page - 1)}
                        >
                          &lt;
                        </Button>
                        <Text fontSize="xs" px={2}>
                          {registry.filters.page} / {registry.employeeList.total_pages}
                        </Text>
                        <Button
                          size="xs"
                          variant="outline"
                          isDisabled={registry.filters.page >= registry.employeeList.total_pages}
                          onClick={() => registry.goToPage(registry.filters.page + 1)}
                        >
                          &gt;
                        </Button>
                      </HStack>
                    </HStack>
                  )}
                </>
              )}
            </Box>
          </VStack>
        </Box>
      </Flex>

      {/* Employee Image Detail Drawer */}
      <Drawer isOpen={isDrawerOpen} placement="right" onClose={registry.clearSelectedEmployee} size="xl">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" noOfLines={1}>{registry.selectedEmployee?.name}</Text>
              <Text fontSize="sm" color="gray.500" fontWeight="normal">
                {registry.selectedEmployee?.nip} · {registry.selectedEmployee?.estate_name} / {registry.selectedEmployee?.division_name}
              </Text>
            </VStack>
          </DrawerHeader>
          <DrawerBody>
            {registry.loadingImages && (
              <Flex justify="center" align="center" h="200px">
                <Spinner color="customGreen.500" />
              </Flex>
            )}
            {registry.imagesError && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {registry.imagesError}
              </Alert>
            )}
            {registry.employeeImages && !registry.loadingImages && (
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.500">
                    {registry.employeeImages.total_images} foto ditemukan
                  </Text>
                  {registry.employeeImages.images.some((img) => img.bounding_box) && (
                    <Badge colorScheme="green" fontSize="10px">Bounding box tersedia</Badge>
                  )}
                </HStack>
                {registry.employeeImages.total_images === 0 ? (
                  <Box py={10} textAlign="center" color="gray.400">
                    Belum ada foto terdaftar
                  </Box>
                ) : (
                  <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={3}>
                    {registry.employeeImages.images.map((img) => (
                      <SaktiFaceRegistryImageViewer
                        key={img.rel_path}
                        relPath={img.rel_path}
                        pose={img.pose}
                        bbox={img.bounding_box}
                        size={160}
                      />
                    ))}
                  </SimpleGrid>
                )}
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Excel Export Modal */}
      <Modal isOpen={isExcelOpen} onClose={onExcelClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Unduh Excel Face Registry</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Estate</FormLabel>
                <Select
                  placeholder="Semua Estate"
                  value={excelEstateOdooId ?? ''}
                  onChange={(e) => handleModalEstateChange(e.target.value)}
                >
                  {registry.estates.map((est) => (
                    <option key={est.id} value={est.odoo_id}>
                      {est.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Divisi</FormLabel>
                <Select
                  placeholder="Semua Divisi"
                  value={excelDivisionOdooId ?? ''}
                  onChange={(e) => setExcelDivisionOdooId(e.target.value ? Number(e.target.value) : undefined)}
                  isDisabled={excelDivisions.length === 0}
                >
                  {excelDivisions.map((div) => (
                    <option key={div.id} value={div.odoo_id}>
                      {div.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium">Jabatan</FormLabel>
                <Select
                  placeholder="Semua Jabatan"
                  value={excelJobPosition ?? ''}
                  onChange={(e) => setExcelJobPosition(e.target.value || undefined)}
                >
                  {registry.jobPositions.map((job) => (
                    <option key={job} value={job}>
                      {job}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onExcelClose} isDisabled={downloading}>
              Batal
            </Button>
            <Button
              colorScheme="green"
              onClick={handleDownloadExcel}
              isLoading={downloading}
              loadingText="Mengunduh"
            >
              Unduh
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SaktiFaceRegistryPage;
